/**
 * Calculate the horizontal scrollLeft value required to bring a child element into view
 * within a scrollable container, taking into account right-to-left layouts and an
 * additional pixel offset.
 *
 * @param container - The scrollable container HTMLElement that defines the viewport.
 * @param element - The child HTMLElement within the container that should be brought into view.
 * @param isRTL - Whether the layout is right-to-left
 * @param offset - Additional pixel offset applied to the computed target
 *
 * @returns A number representing the desired container.scrollLeft to position the element.
 *
 * @example
 * // Scroll element into view with an 8px padding from the container edge in LTR:
 * const target = calculateScrollLeft(container, element, false, 8);
 * container.scrollLeft = target;
 */
const calculateScrollLeft = (
    container: HTMLElement,
    element: HTMLElement,
    isRTL: boolean | undefined,
    offset: number
): number => {
    let targetScrollLeft: number;
    const scrollOffset = offset;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const elementLeftRelativeToContainer = elementRect.left - containerRect.left;

    if (isRTL) {
        const elementRightRelativeToContainer = elementRect.right - containerRect.left;
        targetScrollLeft =
            container.scrollLeft + elementRightRelativeToContainer - container.clientWidth + scrollOffset;
    } else {
        targetScrollLeft = container.scrollLeft + elementLeftRelativeToContainer - scrollOffset;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    return Math.min(targetScrollLeft, maxScrollLeft);
};

export default calculateScrollLeft;
